import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resetPassword } from '../services/authService';
import styles from '../styles/SignIn.module.css';
import headerStyles from '../styles/modules/Header.module.css';

type ResetPasswordResult = {
    ok: boolean;
};

const resetPasswordRequests = new Map<string, Promise<ResetPasswordResult>>();

const resetPasswordOnce = (token: string, newPassword: string): Promise<ResetPasswordResult> => {
    const requestKey = `${token}::${newPassword}`;
    const existingRequest = resetPasswordRequests.get(requestKey);

    if (existingRequest) {
        return existingRequest;
    }

    const request = resetPassword(token, newPassword)
        .then(() => ({
            ok: true
        }))
        .catch(() => ({
            ok: false
        }))
        .finally(() => {
            resetPasswordRequests.delete(requestKey);
        });

    resetPasswordRequests.set(requestKey, request);
    return request;
};

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (!successMessage) {
            return undefined;
        }

        const redirectTimer = window.setTimeout(() => {
            navigate('/signin');
        }, 2000);

        return () => {
            window.clearTimeout(redirectTimer);
        };
    }, [navigate, successMessage]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!token) {
            setError(t('auth.invalidResetToken'));
            return;
        }

        if (newPassword.length < 6) {
            setError(t('auth.passwordTooShort'));
            return;
        }

        if (newPassword !== confirmPassword) {
            setError(t('auth.passwordMismatch'));
            return;
        }

        setLoading(true);

        try {
            const result = await resetPasswordOnce(token, newPassword);

            if (result.ok) {
                setSuccessMessage(t('auth.resetPasswordSuccess'));
                setNewPassword('');
                setConfirmPassword('');
            } else {
                setError(t('auth.resetPasswordFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.signinPage}>
            <header className={styles.header}>
                <button
                    className={`${headerStyles.backButton} ${headerStyles.backButtonAbsolute}`}
                    onClick={() => navigate('/signin')}
                    title={t('auth.backToSignIn')}
                    aria-label={t('auth.backToSignIn')}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1 className={styles.headerTitle}>{t('auth.resetPasswordTitle')}</h1>
            </header>

            <div className={styles.signinContainer}>
                <div className={styles.signinContent}>
                    <div className={styles.signinCard}>
                        <h3 className={styles.cardTitle}>{t('auth.resetPasswordTitle')}</h3>

                        {error && <div className={styles.errorMessage}>{error}</div>}
                        {successMessage && (
                            <div className={styles.signupSection}>
                                <div>{successMessage}</div>
                                <div>{t('auth.redirectingToSignIn')}</div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className={styles.signinForm}>
                            <div className={styles.formGroup}>
                                <label htmlFor="newPassword">{t('auth.newPassword')}</label>
                                <input
                                    type="password"
                                    id="newPassword"
                                    name="newPassword"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                />
                            </div>

                            <button type="submit" className={styles.submitButton} disabled={loading}>
                                {loading ? t('common.loading') : t('auth.resetPasswordCta')}
                            </button>
                        </form>

                        <div className={styles.toggleMode}>
                            <p>
                                <button onClick={() => navigate('/signin')} className={styles.toggleLink}>
                                    {t('auth.backToSignIn')}
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
